/**
 * @package @api-buddy/prisma
 * Prisma database utilities for API Buddy
 */

// Import PrismaClient from the generated client
import { PrismaClient as BasePrismaClient } from '@prisma/client';
import { generateCRUDRoutes, CRUDGenerator, ModelName } from './crud-generator';

// Extend the PrismaClient type to include our custom methods
declare module '@prisma/client' {
  interface PrismaClient {
    $connect: () => Promise<void>;
    $disconnect: () => Promise<void>;
    generateCRUDRoutes: (options: {
      outputDir: string;
      models?: ModelName[];
      exclude?: ModelName[];
      auth?: {
        required?: boolean;
        userIdField?: string;
      };
    }) => Promise<void>;
  }
}

// Create a type that includes our custom methods
type PrismaClientWithExtensions = BasePrismaClient & {
  crud: CRUDGenerator;
  generateCRUDRoutes: (options: {
    outputDir: string;
    models?: ModelName[];
    exclude?: ModelName[];
    auth?: {
      required?: boolean;
      userIdField?: string;
    };
  }) => Promise<void>;
};

/**
 * Extended Prisma client with API Buddy specific functionality
 */
export class PrismaService implements PrismaClientWithExtensions {
  // The underlying Prisma client instance
  private prisma: BasePrismaClient;
  
  // Logger for the service
  private logger: {
    info: (message: string) => void;
    error: (message: string, error?: any) => void;
  };

  /**
   * CRUD generator instance for this client
   */
  public readonly crud: CRUDGenerator;

  // Implement PrismaClient methods
  public $connect: () => Promise<void>;
  public $disconnect: () => Promise<void>;
  
  // For dynamic property access
  [key: string]: any;

  constructor(logger?: any) {
    // Initialize the base Prisma client
    this.prisma = new BasePrismaClient();
    
    // Set up logging
    this.logger = logger || {
      info: (message: string) => console.log(`[Prisma] ${message}`),
      error: (message: string, error?: any) => {
        const errorMessage = error ? `${message}: ${error.message || error}` : message;
        console.error(`[Prisma] ${errorMessage}`);
        if (error?.stack) {
          console.error(error.stack);
        }
      }
    };
    
    // Initialize CRUD generator with the Prisma client instance
    this.crud = new CRUDGenerator(this.prisma);
    
    // Bind Prisma client methods
    this.$connect = this.prisma.$connect.bind(this.prisma);
    this.$disconnect = this.prisma.$disconnect.bind(this.prisma);
    
    // Create a proxy to handle method calls
    return new Proxy(this, {
      get: (target, prop) => {
        // Handle our custom methods and properties first
        if (prop in target) {
          return (target as any)[prop];
        }
        // Delegate to Prisma client
        return (this.prisma as any)[prop];
      }
    }) as unknown as PrismaService;
  }

  /**
   * Connect to the database with error handling
   */
  async connect() {
    try {
      await this.$connect();
      this.logger.info('Successfully connected to the database');
    } catch (error) {
      this.logger.error('Failed to connect to the database', error);
      throw error;
    }
  }

  /**
   * Disconnect from the database
   */
  async disconnect() {
    try {
      await this.$disconnect();
      this.logger.info('Successfully disconnected from the database');
    } catch (error) {
      this.logger.error('Error disconnecting from the database', error);
    }
  }
  
  /**
   * Generate CRUD routes for your Prisma models
   * @param options Generation options
   */
  async generateCRUDRoutes(options: {
    outputDir: string;
    models?: ModelName[];
    exclude?: ModelName[];
    auth?: {
      required?: boolean;
      userIdField?: string;
    };
  }): Promise<void> {
    return this.crud.generateCRUDRoutes({
      outputDir: options.outputDir,
      models: options.models,
      exclude: options.exclude,
      auth: options.auth
    });
  }
}

/**
 * Create a new Prisma service instance
 * @param logger Optional logger implementation
 * @returns PrismaService instance with CRUD capabilities
 */
export function createPrismaService(logger?: any): PrismaService {
  return new PrismaService(logger);
}

// Re-export Prisma client types and utilities
export * from '@prisma/client';

// Export our custom types and utilities
export * from './crud-generator';

// Export the PrismaService as the default export
export default PrismaService;
